import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import PlusOutlined from '@ant-design/icons/PlusOutlined'
import type { CollapseProps, MenuProps } from 'antd'
import {
  Button,
  Collapse,
  ConfigProvider,
  Layout,
  Menu,
  Popover,
  Spin,
  Tag,
  Tooltip,
} from 'antd'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import type { Dispatch, FC, ReactNode, SetStateAction } from 'react'
import React, { useEffect, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'

import { CreateTicketDialog } from './CreateTicket'

type TicketMenuProps = {
  tickets: ReadonlyArray<LibraTicket>
  selectedTicketId?: string
  setSelectedTicketId: Dispatch<SetStateAction<string | undefined>>
  collapsed?: boolean
  extra?: ReadonlyArray<(ticket: LibraTicket) => ReactNode>
  afterTicketCreate?: (createdTicketId: string) => void | Promise<void>
  onDeleteTicket?: (ticketId: string) => Promise<void>
}

function ticketsToMenuItem(
  tickets: ReadonlyArray<LibraTicket>,
  collapsed?: boolean,
  extra?: ReadonlyArray<(ticket: LibraTicket) => ReactNode>,
  onDeleteTicket?: (ticketId: string) => Promise<void>
): MenuProps['items'] {
  return tickets.map((ticket) => ({
    key: ticket.id,
    label: collapsed ? (
      <Tooltip title={ticket.title} placement='right'>
        <FileTextOutlined />
      </Tooltip>
    ) : (
      <TicketLabelWrapper>
        <TicketLabelContainer>
          <TicketTitleRow>
            <FileTextOutlined />
            <TicketTitle>{ticket.title}</TicketTitle>
          </TicketTitleRow>
          {onDeleteTicket && (
            <DeleteTag
              data-role='ticket-delete'
              color='error'
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                void onDeleteTicket(ticket.id)
              }}>
              <DeleteOutlined />
            </DeleteTag>
          )}
        </TicketLabelContainer>
        {extra && extra.length > 0 && (
          <TicketExtraRow>
            {extra.map((renderExtra, index) => (
              <React.Fragment key={index}>{renderExtra(ticket)}</React.Fragment>
            ))}
          </TicketExtraRow>
        )}
      </TicketLabelWrapper>
    ),
  }))
}

const RawTicketMenu: FC<TicketMenuProps> = ({
  tickets,
  selectedTicketId,
  setSelectedTicketId,
  collapsed,
  extra,
  onDeleteTicket,
}) => {
  const theme = useTheme()
  const [activeKey, setActiveKey] = useState<'open' | 'closed'>('open')

  const openTicketIds = useMemo(
    () =>
      new Set(
        tickets
          .filter((ticket) => !ticket.workflowComplete)
          .map((ticket) => ticket.id)
      ),
    [tickets]
  )

  const selectedTicket = useMemo(() => {
    return tickets.find((ticket) => ticket.id === selectedTicketId)
  }, [selectedTicketId, tickets])

  useEffect(() => {
    if (!selectedTicket) {
      return
    }
    setActiveKey(selectedTicket.workflowComplete ? 'closed' : 'open')
  }, [selectedTicket])

  const closedTicketIds = useMemo(
    () =>
      new Set(
        tickets
          .map((ticket) => ticket.id)
          .filter((id) => !openTicketIds.has(id))
      ),
    [openTicketIds, tickets]
  )

  const collapseItems = useMemo<CollapseProps['items']>(
    () => [
      {
        key: 'open',
        label: (
          <CollapseHeaderContainer>
            <span>{collapsed ? 'O' : 'Open Tickets'}</span>
            <Tag color='green'>{openTicketIds.size}</Tag>
          </CollapseHeaderContainer>
        ),
        children: (
          <TicketMenuAccordionContent
            collapsed={collapsed}
            tickets={tickets.filter((ticket) => openTicketIds.has(ticket.id))}
            selectedTicketId={
              selectedTicketId && openTicketIds.has(selectedTicketId)
                ? selectedTicketId
                : undefined
            }
            setSelectedTicketId={setSelectedTicketId}
            extra={extra}
            onDeleteTicket={onDeleteTicket}
          />
        ),
      },
      {
        key: 'closed',
        label: (
          <CollapseHeaderContainer>
            <span>{collapsed ? 'C' : 'Closed Tickets'}</span>
            <Tag color='gray'>{closedTicketIds.size}</Tag>
          </CollapseHeaderContainer>
        ),
        children: (
          <TicketMenuAccordionContent
            collapsed={collapsed}
            tickets={tickets.filter((ticket) => closedTicketIds.has(ticket.id))}
            selectedTicketId={
              selectedTicketId && closedTicketIds.has(selectedTicketId)
                ? selectedTicketId
                : undefined
            }
            setSelectedTicketId={setSelectedTicketId}
            extra={extra}
            onDeleteTicket={onDeleteTicket}
          />
        ),
      },
    ],
    [
      closedTicketIds,
      collapsed,
      extra,
      openTicketIds,
      onDeleteTicket,
      selectedTicketId,
      setSelectedTicketId,
      tickets,
    ]
  )

  return (
    <ConfigProvider
      theme={{
        components: {
          Menu: {
            itemSelectedBg: theme.forecast.background3,
            itemActiveBg: theme.forecast.background3,
            itemSelectedColor: theme.forecast.activeText,
          },
        },
      }}>
      <StyledCollapse
        items={collapseItems}
        accordion
        activeKey={activeKey}
        onChange={([key]) =>
          setActiveKey((prev) =>
            key === undefined ? prev : key === 'closed' ? 'closed' : 'open'
          )
        }
      />
    </ConfigProvider>
  )
}

export const TicketMenuAccordionContent: FC<TicketMenuProps> = ({
  tickets,
  selectedTicketId,
  setSelectedTicketId,
  collapsed,
  extra,
  onDeleteTicket,
}) => {
  const items = useMemo(
    () => ticketsToMenuItem(tickets, collapsed, extra, onDeleteTicket),
    [tickets, collapsed, extra, onDeleteTicket]
  )

  if (!items?.length) {
    return <TicketMenuPlaceholder>No tickets</TicketMenuPlaceholder>
  }

  return (
    <StyledTicketMenu
      mode='inline'
      selectedKeys={selectedTicketId ? [selectedTicketId] : []}
      items={items}
      onClick={({ key }) => setSelectedTicketId(String(key))}
    />
  )
}

export const CollapseHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

export const TicketMenuPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const StyledTicketMenu = styled(Menu)`
  border-inline-end: none !important;
  background: transparent !important;
  height: 100%;

  .ant-menu-item {
    height: 100%;
  }

  .ant-menu-item:hover {
    [data-role='ticket-delete'] {
      opacity: 1;
      pointer-events: auto;
    }
  }
`

const StyledCollapse = styled(Collapse)`
  height: 100%;
  .ant-collapse-content {
    height: 100%;
  }
`

const TicketLabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const TicketLabelContainer = styled.span`
  display: flex;
  gap: 0.5rem;
  height: 1.8rem;
  align-items: center;
  justify-content: space-between;
`

const TicketTitleRow = styled.span`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
`

const TicketTitle = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const DeleteTag = styled(Tag)`
  opacity: 0;
  pointer-events: none;
  cursor: pointer;
  transition:
    opacity 120ms ease,
    filter 120ms ease,
    background-color 120ms ease,
    border-color 120ms ease;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-inline-end: 0;
  padding-inline: 0.25rem;

  &:hover {
    filter: brightness(0.95);
  }
`
const TicketExtraRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding-bottom: 0.25rem;
`

export const TicketMenu = React.memo(RawTicketMenu)

type TicketMenuSiderProps = TicketMenuProps & {
  organizationId?: string
  width: number
  collapsedWidth: number
  isLoading: boolean
  header?: ReactNode
  collapseSidebarOnSelect?: boolean
}

const RawTicketMenuSider: FC<TicketMenuSiderProps> = ({
  organizationId,
  width,
  collapsedWidth,
  isLoading,
  selectedTicketId,
  header,
  collapseSidebarOnSelect = true,
  afterTicketCreate,
  ...rest
}) => {
  const [collapsed, setCollapsed] = useState(collapseSidebarOnSelect)

  useEffect(() => {
    if (!collapseSidebarOnSelect) {
      return
    }
    setCollapsed(selectedTicketId ? true : false)
  }, [collapseSidebarOnSelect, selectedTicketId])

  return (
    <StyledSider
      width={width}
      collapsible
      collapsed={collapsed}
      onCollapse={setCollapsed}
      collapsedWidth={collapsedWidth}>
      <SiderInner>
        <SiderMain>
          {!collapsed && header}
          {isLoading ? (
            <TicketMenuPlaceholder>
              <Spin size='small' />
            </TicketMenuPlaceholder>
          ) : (
            <TicketMenu
              selectedTicketId={selectedTicketId}
              collapsed={collapsed}
              {...rest}
            />
          )}
        </SiderMain>
        {!collapsed && (
          <SiderFooter>
            {organizationId ? (
              <Popover
                trigger='hover'
                title='Create Ticket'
                placement='topRight'
                mouseEnterDelay={0}
                mouseLeaveDelay={0.2}
                content={
                  <CreateTicketDialog
                    organizationId={organizationId}
                    afterCreate={afterTicketCreate}
                  />
                }>
                <CreateTicketButton
                  type='primary'
                  shape='circle'
                  icon={<PlusOutlined />}
                  aria-label='Create ticket'
                />
              </Popover>
            ) : (
              <Tooltip title='No organization selected'>
                <span>
                  <CreateTicketButton
                    type='primary'
                    shape='circle'
                    icon={<PlusOutlined />}
                    aria-label='Create ticket'
                    disabled
                  />
                </span>
              </Tooltip>
            )}
          </SiderFooter>
        )}
      </SiderInner>
    </StyledSider>
  )
}

export const TicketMenuSider = React.memo(RawTicketMenuSider)

const StyledSider = styled(Layout.Sider)`
  background: ${({ theme }) => theme.forecast.background2} !important;
  .ant-layout-sider-trigger {
    background: ${({ theme }) => theme.forecast.background2} !important;
    color: ${({ theme }) => theme.forecast.activeText};
    border-top: 1px solid ${({ theme }) => theme.forecast.background3};
  }
`

const SiderInner = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`

const SiderMain = styled.div`
  flex: 1 1 auto;
  min-height: 0;
`

const SiderFooter = styled.div`
  display: flex;
  height: 40px;
  justify-content: center;
  align-items: center;
`

const CreateTicketButton = styled(Button)`
  padding: 0.5rem;
`
